import { forwardRef, chakra, useMergeRefs } from '@chakra-ui/react';
import type { HTMLChakraProps, ChakraComponent } from '@chakra-ui/react';
import { ariaAttr } from '@chakra-ui/utils';
import { useCallback, useMemo, useRef, useState } from 'react';
import type {
  ChangeEventHandler,
  FocusEventHandler,
  HTMLAttributes,
  KeyboardEventHandler,
} from 'react';

import { assertIsFunction } from '@frontend/utils';

import { useSearchInputStyles } from '../SearchInputStylesContext';
import { useSearchInputContext } from '../utils/SearchInputContext';
import { useHintToShow } from '../utils/useHintToShow';
import { Hint } from './Hint';
import { HintLoadingSpinner } from './HintLoadingSpinner';
import { HintWrapper } from './HintWrapper';

export type SearchInputFieldProps = HTMLChakraProps<'input'> & {
  hints?: string[] | null;
  isLoadingHint?: boolean;
};

export const SearchInputField = forwardRef<SearchInputFieldProps, 'input'>(
  ({ hints, isLoadingHint, placeholder, ...restProps }, forwardedRef) => {
    const {
      value: valueProp,
      onChange: onChangeProp,
      onFocus: onFocusProp,
      onBlur: onBlurProp,
      onSubmit,
      paddingLeft,
      paddingRight,
      mode,
      isDisabled,
      isInvalid,
      isReadOnly,
      'aria-describedby': ariaDescBy,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
    } = useSearchInputContext();

    const inputRef = useRef<HTMLInputElement>(null);
    const mergedRefs = useMergeRefs(forwardedRef, inputRef);

    const getIsFocused = (): boolean =>
      !!document.activeElement && document.activeElement === inputRef.current;

    const [isFocused, setIsFocused] = useState<boolean>(getIsFocused());

    const [internalValue, setInternalValue] = useState<string | undefined>(
      valueProp,
    );

    const value = valueProp ?? internalValue;

    const onChange: ChangeEventHandler<HTMLInputElement> = useCallback(
      (event) => {
        setInternalValue(event.target.value);
        onChangeProp?.(event);
      },
      [setInternalValue, onChangeProp],
    );

    const onFocus: FocusEventHandler<HTMLInputElement> = useCallback(
      (event) => {
        setIsFocused(true);
        onFocusProp?.(event);
      },
      [setIsFocused, onFocusProp],
    );

    const onBlur: FocusEventHandler<HTMLInputElement> = useCallback(
      (event) => {
        setIsFocused(false);
        onBlurProp?.(event);
      },
      [setIsFocused, onBlurProp],
    );

    const { hintToShow } = useHintToShow({
      mode,
      hints,
      value,
    });

    const { field: fieldStyles } = useSearchInputStyles();
    const searchType: HTMLAttributes<HTMLInputElement>['inputMode'] = 'search';

    const hintLoadingSpinnerIsDisplayed = !!value && !!isLoadingHint;

    const hintIsDisplayed: boolean =
      !isLoadingHint && isFocused && !!hintToShow;

    const fillHint = useCallback(() => {
      const inputElement = inputRef.current;

      if (inputElement instanceof HTMLInputElement) {
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          'value',
        )?.set;
        assertIsFunction(nativeInputValueSetter);

        const newValue = `${value ?? ''}${hintToShow ?? ''}`;

        // To update the value, since the SearchInput might or might not be
        // controlled by its calling context, we set the value on the HTML input
        // element, set the internal value, and then dispatch a change event
        // which triggers any onChange handlers associated with the input.
        nativeInputValueSetter.call(inputElement, newValue);

        setInternalValue(newValue);

        const changeEvent = new Event('change', {
          bubbles: true,
        });

        inputElement.dispatchEvent(changeEvent);
      }
    }, [value, hintToShow]);

    const handleFillHint: KeyboardEventHandler = useCallback(
      (event) => {
        const { altKey, ctrlKey, metaKey, shiftKey } = event;

        const anyModifierIsPressed = altKey || ctrlKey || metaKey || shiftKey;

        if (!anyModifierIsPressed && hintIsDisplayed) {
          event.preventDefault();
          fillHint();
        }
      },
      [hintIsDisplayed, fillHint],
    );

    const handleKeyPress: KeyboardEventHandler = useCallback(
      (event) => {
        if (event.key === 'Enter') {
          onSubmit?.();
        }
      },
      [onSubmit],
    );

    const handleKeyDown: KeyboardEventHandler = useCallback(
      (event) => {
        // Need to use onKeyDown to capture tab key because the default tab
        // behavior causes the input to blur before onKeyPress fires.
        if (event.key === 'Tab' || event.key === 'ArrowRight') {
          handleFillHint(event);
        }
      },
      [handleFillHint],
    );

    const ariaInvalid = ariaAttr(isInvalid);

    const inputProps: Parameters<ChakraComponent<'input'>>[0] = useMemo(
      () => ({
        'aria-invalid': ariaInvalid,
        'aria-readonly': isReadOnly,
        'aria-describedby': ariaDescBy,
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledBy,
        inputMode: searchType,
        type: 'text',
        disabled: isDisabled,
        readOnly: isReadOnly,
        placeholder,
        value,
        onChange,
        onFocus,
        onBlur,
        onKeyPress: handleKeyPress,
        onKeyDown: handleKeyDown,
      }),
      [
        ariaInvalid,
        isReadOnly,
        ariaDescBy,
        ariaLabel,
        ariaLabelledBy,
        searchType,
        isDisabled,
        placeholder,
        value,
        onChange,
        onFocus,
        onBlur,
        handleKeyPress,
        handleKeyDown,
      ],
    );

    return (
      <>
        <chakra.input
          __css={{
            ...fieldStyles,
            pl: paddingLeft,
            pr: paddingRight,
          }}
          {...inputProps}
          {...restProps}
          ref={mergedRefs}
        />
        {(hintLoadingSpinnerIsDisplayed || hintIsDisplayed) && (
          <HintWrapper value={value}>
            {hintLoadingSpinnerIsDisplayed ? (
              <HintLoadingSpinner />
            ) : (
              hintIsDisplayed && <Hint hintToShow={hintToShow} />
            )}
          </HintWrapper>
        )}
      </>
    );
  },
);
SearchInputField.displayName = 'SearchInputField';
